import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { handlePaymentWithCurrency } from '../../utils/paymentUtils';
import BillingPeriodToggle from '../ui/BillingPeriodToggle';
import CurrencyModal from '../ui/CurrencyModal';
import PricingCards from '../ui/PricingCards';

const UpgradePlans: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<null | ("Plus" | "Pro" | "Topup")>(null);
  const [billingPeriod, setBillingPeriod] = useState('4month');

  // Handle currency selection using shared payment utility
  const handleCurrencySelect = async (currency: 'USD' | 'INR') => {
    if (!selectedPlan || !currentUser) return;

    setIsLoading(selectedPlan);
    try {
      const paymentLink = await handlePaymentWithCurrency(currency, {
        plan: selectedPlan,
        billingPeriod,
        userId: currentUser.uid
      });

      window.open(paymentLink, '_blank');
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to generate payment link. Please try again.');
    } finally {
      setIsLoading(null);
      setShowCurrencyModal(false);
    }
  };

  const handlePlanButtonClick = (plan: "Free" | "Plus" | "Pro" | "Topup") => {
    if (plan === 'Free') return; // Free plan doesn't need action

    if (!currentUser) {
      navigate('/login');
      return;
    }
    setSelectedPlan(plan as "Plus" | "Pro" | "Topup");
    setShowCurrencyModal(true);
  };

  return (
    <div className="min-h-screen bg-accent-sand py-8 sm:py-12 px-3 sm:px-4 lg:px-8">
      <CurrencyModal
        showModal={showCurrencyModal}
        isLoading={isLoading !== null}
        onCurrencySelect={handleCurrencySelect}
        onClose={() => setShowCurrencyModal(false)}
      />
      <div className="max-w-7xl mx-auto">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-text-heading">
            Choose Your Plan
          </h1>
          <p className="mt-3 sm:mt-4 text-lg sm:text-xl text-text-description">
            Select the perfect plan for your needs
          </p>

          {/* Billing Period Toggle */}
          <div className="mt-6 sm:mt-8">
            <BillingPeriodToggle
              billingPeriod={billingPeriod}
              setBillingPeriod={setBillingPeriod}
            />
          </div>
        </div>

        <div className="mt-8 sm:mt-12 px-2 sm:px-0">
          <PricingCards
            billingPeriod={billingPeriod}
            paymentLoading={isLoading}
            onPlanAction={handlePlanButtonClick}
            variant="upgrade"
          />
        </div>

        <div className="mt-8 sm:mt-12 text-center">
          <p className="text-sm sm:text-base text-text-description">
            Need a custom plan?{' '}
            <Link to="/contact" className="text-secondary-600 hover:text-secondary-500">
              Contact us
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradePlans; 